import React, { memo, useCallback, useEffect, useState } from 'react'
import tw from 'twin.macro'
import { TabsSelector } from 'src/components/shared/tabs-selector/TabsSelector'
import type { TabsSelectorOption } from 'src/components/shared/tabs-selector/TabsSelector.types'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { Typography } from '@mui/material'
import { SectionCardSkeleton } from 'src/components/shared/SectionCardSkeleton'
import { PaymentConfigEmptyCard } from 'src/components/customer/payments/PaymentConfigEmptyCard'
import { PayoutSettingsForm } from 'src/components/customer/payments/payout-settings/PayoutSettingsForm'
import { VendorFeesForm } from 'src/components/customer/payments/vendor-fees/VendorFeesForm'
import {
  AdminFetchPaymentConfig,
  AdminSavePayoutSettings,
  AdminSaveVendorFees,
} from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import type { SavePayoutSettingsPayload, SaveVendorFeesPayload } from 'src/interfaces/payment'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import { getCustomer } from 'src/selectors/customer'
import {
  getPaymentConfig,
  getPaymentConfigError,
  getPaymentConfigLoaded,
  getPaymentConfigPartnerId,
} from 'src/selectors/paymentConfig'
import { resolveCustomerDisplayName } from 'src/utils/customerList'

type PaymentsTab = 'payout-settings' | 'vendor-fees'

const PAYMENTS_TABS: TabsSelectorOption<PaymentsTab>[] = [
  { label: 'Payout Settings', value: 'payout-settings' },
  { label: 'GigSafe Vendor Fees', value: 'vendor-fees' },
]

const CustomerPaymentsPageComponent = () => {
  const [selectedTab, setSelectedTab] = useState<PaymentsTab>('payout-settings')
  const { customerId } = useParams<{ customerId: string }>()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const snackbar = useSnackbar()

  const { isAuthenticated } = useAuthSession()
  const customer = useSelector(getCustomer)
  const config = useSelector(getPaymentConfig)
  const isLoaded = useSelector(getPaymentConfigLoaded)
  const error = useSelector(getPaymentConfigError)
  const loadedPartnerId = useSelector(getPaymentConfigPartnerId)
  const isReady = isLoaded && loadedPartnerId === customerId
  // Both forms save through PUT /partners/:id/buyer — `payout_settings_manage`. Without it both
  // render read-only (ENG-1207 part 2).
  const canManagePayouts = useHasPermission(InternalPermission.PayoutSettingsManage)

  useEffect(() => {
    if (!isAuthenticated || !customerId) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchPaymentConfig.request({ partnerId: customerId }))
  }, [isAuthenticated, customerId, asyncDispatchWithErrorToast])

  // Returns whether the save landed: the form's confirm modal stays open on a failure so the
  // admin can retry against the values they just confirmed. `asyncDispatchWithErrorToast`
  // resolves null and raises the error toast on failure, so this never throws.
  const handleSavePayoutSettings = useCallback(
    async (payload: SavePayoutSettingsPayload): Promise<boolean> => {
      const saved = await asyncDispatchWithErrorToast(AdminSavePayoutSettings.request(payload))

      if (!saved) {
        return false
      }

      snackbar?.enqueueSnackbar('Payout settings updated', { variant: 'success' })

      return true
    },
    [asyncDispatchWithErrorToast, snackbar],
  )

  const handleSaveVendorFees = useCallback(
    async (payload: SaveVendorFeesPayload): Promise<boolean> => {
      const saved = await asyncDispatchWithErrorToast(AdminSaveVendorFees.request(payload))

      if (!saved) {
        return false
      }

      snackbar?.enqueueSnackbar('Vendor fees updated', { variant: 'success' })

      return true
    },
    [asyncDispatchWithErrorToast, snackbar],
  )

  if (!customerId) {
    return null
  }

  const customerName = customer ? resolveCustomerDisplayName(customer) : 'this customer'

  return (
    <div data-testid="admin-customer-payments-page" css={tw`mx-auto w-full max-w-[720px] p-6`}>
      <Typography variant="h2" css={tw`mb-5`}>
        Payments
      </Typography>

      {error && !isReady ? (
        <Typography variant="body2" css={tw`text-critical-500`}>
          {error}
        </Typography>
      ) : null}

      {!isReady && !error ? (
        <SectionCardSkeleton fields={4} dataTestId="admin-customer-payments-loading" />
      ) : null}

      {isReady && config === null ? <PaymentConfigEmptyCard /> : null}

      {isReady && config !== null ? (
        <div css={tw`flex flex-col gap-6`}>
          <TabsSelector<PaymentsTab>
            options={PAYMENTS_TABS}
            selectedValue={selectedTab}
            onSelect={setSelectedTab}
            testIdPrefix="admin-customer-payments"
          />
          {/* Only the selected form is mounted, so switching tabs drops unsaved edits in the
              other one — the same trade-off as the dashboard's Payments tabs. */}
          {selectedTab === 'payout-settings' ? (
            <PayoutSettingsForm
              config={config}
              partnerId={customerId}
              customerName={customerName}
              isReadOnly={!canManagePayouts}
              onSubmit={handleSavePayoutSettings}
            />
          ) : (
            <VendorFeesForm
              config={config}
              partnerId={customerId}
              customerName={customerName}
              isReadOnly={!canManagePayouts}
              onSubmit={handleSaveVendorFees}
            />
          )}
        </div>
      ) : null}
    </div>
  )
}

export const CustomerPaymentsPage = memo(CustomerPaymentsPageComponent)
